import React, { useState, useCallback } from "react";
import ReactFlow, {
  Background, Controls, useNodesState, useEdgesState, Panel,
} from "reactflow";
import "reactflow/dist/style.css";

const NODE_STYLES = {
  main:        { bg: "#FF2D78", text: "#fff", border: "#FF2D78" },
  patho:       { bg: "#00C8E8", text: "#fff", border: "#00C8E8" },
  nursing_dx:  { bg: "#12172B", text: "#fff", border: "rgba(255,255,255,0.7)" },
  intervention:{ bg: "#00D084", text: "#fff", border: "#00D084" },
  complication:{ bg: "#CC0000", text: "#fff", border: "#CC0000" },
  medication:  { bg: "#F5E200", text: "#12172B", border: "#F5E200" },
  education:   { bg: "#7B2FBE", text: "#fff", border: "#7B2FBE" },
};

const LEGEND = [
  ["main", "Diagnosis"], ["patho", "Pathophysiology"], ["nursing_dx", "Nursing Dx"],
  ["intervention", "Intervention"], ["complication", "Complication"],
  ["medication", "Medication"], ["education", "Education"],
];

function layoutNodes(nodes) {
  if (!nodes || !nodes.length) return [];
  const main = nodes.find(n => n.type === "main");
  const others = nodes.filter(n => n.type !== "main");
  const cx = 400, cy = 280, r = 240;
  const result = [];
  if (main) result.push({ ...main, position: { x: cx - 80, y: cy - 30 } });
  others.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / others.length - Math.PI / 2;
    result.push({ ...node, position: { x: cx + r * Math.cos(angle) - 80, y: cy + r * Math.sin(angle) - 30 } });
  });
  return result;
}

function toRFNodes(rawNodes) {
  return layoutNodes(rawNodes).map(node => {
    const s = NODE_STYLES[node.type] || NODE_STYLES.main;
    return {
      id: node.id,
      position: node.position,
      data: { label: node.label, details: node.details, type: node.type },
      style: {
        background: s.bg, color: s.text,
        border: `2px solid ${s.border}`,
        borderRadius: 10, padding: "8px 12px",
        fontSize: 11, fontWeight: 700,
        fontFamily: "'Poppins', sans-serif",
        width: 150, textAlign: "center",
        cursor: "pointer", lineHeight: 1.3,
      },
    };
  });
}

function toRFEdges(rawEdges) {
  return (rawEdges || []).map(e => ({
    id: e.id, source: e.source, target: e.target,
    style: { stroke: "rgba(255,255,255,0.25)", strokeWidth: 1.5 },
    animated: e.type === "patho",
  }));
}

export default function ConceptMapView({ data }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(() => toRFNodes(data.nodes));
  const [edges, , onEdgesChange] = useEdgesState(() => toRFEdges(data.edges));
  const [selected, setSelected] = useState(null);

  const onNodeClick = useCallback((_, node) => setSelected(node.data), []);

  const resetLayout = useCallback(() => {
    setNodes(toRFNodes(data.nodes));
    setSelected(null);
  }, [data.nodes, setNodes]);

  return (
    <div style={{ position: "relative", background: "#1A2040", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
      {/* Title bar */}
      <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)", fontWeight: 900, color: "#fff", fontSize: 15, fontFamily: "'Poppins',sans-serif" }}>{data.title}</div>

      {/* Flow canvas */}
      <div style={{ height: 420 }}>
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView fitViewOptions={{ padding: 0.2 }}
          style={{ background: "#1A2040" }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="rgba(255,255,255,0.03)" gap={20} />
          <Controls style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8 }} />
          <Panel position="top-right">
            <button onClick={resetLayout} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, padding: "5px 10px", color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "'Poppins',sans-serif", fontWeight: 600 }}>↺ Reset</button>
          </Panel>
        </ReactFlow>
      </div>

      {/* Legend */}
      <div style={{ padding: "6px 14px", display: "flex", gap: 10, flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.2)" }}>
        {LEGEND.map(([type, label]) => (
          <div key={type} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: "rgba(255,255,255,.6)", fontFamily: "'Poppins',sans-serif" }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: NODE_STYLES[type].bg, flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(18,23,43,0.97)", borderTop: "1px solid rgba(255,255,255,0.12)", padding: "12px 16px", maxHeight: 160, overflowY: "auto", zIndex: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontWeight: 800, color: "#fff", fontSize: 13, fontFamily: "'Poppins',sans-serif" }}>{selected.label}</div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>✕</button>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.75)", lineHeight: 1.65, fontFamily: "'Poppins',sans-serif" }}>{selected.details}</div>
        </div>
      )}
    </div>
  );
}
